import { useState, useEffect, useCallback } from 'react';
import Block from '../../../components/Block/Block';
import ProgramBlock from '../ProgramBlock/ProgramBlock';
import Button from '../../../components/Button/Button';
import FadeIn from '../../../components/FadeIn/FadeIn';
import Modal from '../../../components/Modal/Modal';
import OnlineRecordingForm from '../../../components/OnlineRecordingForm/OnlineRecordingForm';
import { fetchSports, fetchBranches, fetchPrograms, fetchBranchesBySport } from '../../../services/api';
import style from './programlistblock.module.scss';

const ProgramListBlock = () => {
  const [modal, setModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [sports, setSports] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showBranches, setShowBranches] = useState(false);
  const [branchesForSport, setBranchesForSport] = useState([]);
  const [selectedSport, setSelectedSport] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load sports and branches data
  useEffect(() => {
    const loadFilters = async () => {
      try {
        setLoading(true);
        const [sportsRes, branchesRes] = await Promise.all([
          fetchSports({ populate: '*' }),
          fetchBranches({ populate: '*' })
        ]);
        setSports(sportsRes.data);
        setBranches(branchesRes.data);
      } catch (err) {
        console.error('Error loading filters:', err);
        setError('Не удалось загрузить фильтры');
      } finally {
        setLoading(false);
      }
    };

    loadFilters();
  }, []);

  // Load programs based on selected filters
  useEffect(() => {
    const loadPrograms = async () => {
      try {
        setLoading(true);
        const filters = {};
        
        if (selectedSport) {
          filters.sport = selectedSport;
        }
        
        if (selectedBranch) {
          filters.branches = selectedBranch;
        }

        const { data } = await fetchPrograms({ 
          populate: '*',
          filters: Object.keys(filters).length ? filters : undefined
        });
        setPrograms(data);
      } catch (err) {
        console.error('Error loading programs:', err);
        setError('Не удалось загрузить программы');
      } finally {
        setLoading(false);
      }
    };

    loadPrograms();
  }, [selectedSport, selectedBranch]);

  const loadBranchesForSport = async (sportId) => {
    try {
      setLoading(true);
      const response = await fetchBranchesBySport(sportId);
      setBranchesForSport(response);
      setShowBranches(true);
    } catch (err) {
      console.error('Error loading branches:', err);
      setError('Не удалось загрузить районы');
    } finally {
      setLoading(false);
    }
  };

  const handleSportSelect = (sportId) => {
    if (selectedSport === sportId) {
      setSelectedSport(null);
      setShowBranches(false);
      setSelectedBranch(null);
    } else {
      setSelectedSport(sportId);
      loadBranchesForSport(sportId);
      setSelectedBranch(null);
    }
  };

  const handleBranchSelect = (branchId) => {
    setSelectedBranch(branchId === selectedBranch ? null : branchId);
  };

  const handleSignUpClick = useCallback((programData) => {
    setSelectedProgram({
      program: programData.program,
      availableBranches: programData.branches.map(b => ({
        id: b.id,
        Title: b.attributes?.Title,
        Address: b.attributes?.Address
      }))
    });
    setModal(true);
  }, []);

  const resetFilters = () => {
    setSelectedSport(null);
    setSelectedBranch(null);
    setShowBranches(false);
  };

  const isAllSelected = !selectedSport && !selectedBranch;

  return (
    <Block
      flexDirection="column"
      justifyContent="start"
      alignItems="start"
      width="75%"
      height='fit-content'
      background="rgba(255, 255, 255, 0.85)"
    >
      <FadeIn>
        <div className={style.content}>
          {/* Filter Section */}
          <div className={style.wrapperFilter}>
            <div className={style.filterHeader}>
              <Button
                className={style.allProgramsBtn}
                width='auto'
                minWidth='160px'
                height='45px'
                text='Все программы'
                color={isAllSelected ? '#fff' : '#1d3e8c'}
                background={isAllSelected ? '#1d3e8c' : '#fff'}
                border={isAllSelected ? 'none' : '1.5px solid #1d3e8c'}
                fontFamily='Montserrat, sans-serif'
                fontSize='0.95rem'
                fontWeight='600'
                onClick={resetFilters}
                style={{
                  boxShadow: isAllSelected ? '0 4px 12px rgba(29, 62, 140, 0.3)' : 'none',
                  transition: 'all 0.3s ease'
                }}
              />
              
              {selectedSport && (
                <Button
                  width='auto'
                  height='45px'
                  text='Очистить фильтры'
                  color='#6b7a8f'
                  background='transparent'
                  border='none'
                  fontFamily='Montserrat, sans-serif'
                  fontSize='0.85rem'
                  fontWeight='500'
                  onClick={resetFilters}
                  style={{
                    opacity: 0.7,
                    transition: 'opacity 0.3s ease',
                    padding: '0 16px'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '1'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                />
              )}
            </div>

            {/* Sport Filters */}
            <div className={style.filterGroup}>
              {sports.map(sport => (
                <Button
                  key={sport.id}
                  className={style.filterButton}
                  width='auto'
                  minWidth='120px'
                  height='45px'
                  text={sport.attributes.Title}
                  color={selectedSport === sport.id ? '#fff' : '#1d3e8c'}
                  background={selectedSport === sport.id ? '#1d3e8c' : '#fff'}
                  border={selectedSport === sport.id ? 'none' : '1.5px solid #1d3e8c'}
                  fontFamily='Montserrat, sans-serif'
                  fontSize='0.9rem'
                  fontWeight='500'
                  onClick={() => handleSportSelect(sport.id)}
                  style={{
                    boxShadow: selectedSport === sport.id ? '0 4px 12px rgba(29, 62, 140, 0.25)' : 'none',
                    transition: 'all 0.3s ease'
                  }}
                />
              ))}
            </div>

            {/* Branches Section */}
            {showBranches && branchesForSport.length > 0 && (
              <div className={style.branchesSection}>
                <h4>📍 Доступно в районах</h4>
                <div className={style.branchesList}>
                  {branchesForSport.map(branch => (
                    <Button
                      key={branch.id}
                      className={style.branchButton}
                      width='auto'
                      minWidth='100px'
                      height='40px'
                      text={branch.attributes.Title}
                      color={selectedBranch === branch.id ? '#fff' : '#1d3e8c'}
                      background={selectedBranch === branch.id ? '#1d3e8c' : '#fff'}
                      border={selectedBranch === branch.id ? 'none' : '1.5px solid #1d3e8c'}
                      fontFamily='Montserrat, sans-serif'
                      fontSize='0.85rem'
                      fontWeight='500'
                      onClick={() => handleBranchSelect(branch.id)}
                      style={{
                        boxShadow: selectedBranch === branch.id ? '0 4px 12px rgba(29, 62, 140, 0.2)' : 'none',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Programs List */}
          <div className={style.wrapperProgramBlocks}>
            {loading ? (
              <div className={style.loadingState}>
                <div className={style.spinner}></div>
              </div>
            ) : error ? (
              <div className={style.errorState}>
                <p>⚠️ {error}</p>
              </div>
            ) : programs.length === 0 ? (
              <div className={style.emptyState}>
                <p>По выбранным фильтрам программы не найдены</p>
              </div>
            ) : (
              programs.map(program => (
                <ProgramBlock 
                  key={program.id}
                  program={{
                    ...program.attributes,
                    id: program.id
                  }}
                  onSignUpClick={handleSignUpClick}
                />
              ))
            )}
          </div>
        </div>
      </FadeIn>
      
      {/* Sign Up Modal */}
      <Modal
        active={modal} 
        setActive={setModal}
        width='fit-content'
        height='fit-content'
        padding='0'
      >
        {selectedProgram && (
          <OnlineRecordingForm 
            program={selectedProgram?.program}
            availableBranches={selectedProgram.availableBranches}
            onSuccess={() => setModal(false)}
          />
        )}
      </Modal>
    </Block>
  );
};

export default ProgramListBlock;